import React, { useEffect } from 'react';

import CRMApi from '../../../Api/CRM/CRMApi';
import Admin from '../../../Api/Models/Admin';
import useGlobalState from '../../GlobalStateContext';
import DeleteButton from '../../elements/DeleteButton';
import DetachElement from '../../elements/DetachElement';
import Search from '../../elements/Search';
import Card from '../../elements/common/Card';
import TableHead from '../../elements/common/TableHead';
import TableLoader from '../../elements/common/TableLoader';
import Paginator from '../../elements/paginator';
import { PERMISSION } from '../../helpers/Permissions';
import useAutoPaginator from '../../hooks/useAutoPaginator';
import { ROLES } from '../../services/AdminPolicyService';

import AdminCreate from './AdminCreate';
import AdminEdit from './AdminEdit';
import AdminShow from './AdminShow';

import LinkWithParams from 'crm/elements/LinkWithParams';
import ListItemModalManager from 'crm/elements/ListItemModalManager';
import { getListNameFromUrl } from 'crm/helpers/UrlHelper';

type Props = {
    cardTitle?: string;
    onlySuperadmins?: boolean;
} & (
    | { source?: 'admins' | 'super-admins'; relatedId?: never; updateTrigger?: number }
    | {
          source: 'role' | 'permission' | 'affiliate_owner' | 'reseller' | 'super_agent' | 'super-admins';
          relatedId: number;
          updateTrigger?: number;
      }
);

export default function AdminsList({
    source = 'admins',
    relatedId,
    cardTitle,
    onlySuperadmins = false,
    updateTrigger,
}: Props) {
    const { loggedAdmin } = useGlobalState();
    const url = source === 'admins' || source === 'super-admins' ? 'admin' : `${source}/${relatedId}/admins`;
    const { items, updateItems, isLoading, auto } = useAutoPaginator<Admin>(async (page, params) => {
        // TODO: create AdminsApi
        const newParams = { ...params, page, only_superadmins: onlySuperadmins ? true : undefined };

        return (await CRMApi.get(url, { params: newParams })).data;
    });
    const listName = getListNameFromUrl(url);

    useEffect(() => {
        updateItems();
    }, [updateTrigger]);

    function getLinks(admin: Admin, listName: string) {
        return source !== 'admins' && source !== 'super-admins' ? (
            <DetachElement
                url={`/crea/api/${url}`}
                params={{ admin: admin.id }}
                action={updateItems}
                roles={ROLES.MAIN_ADMINS}
            />
        ) : (
            <>
                {loggedAdmin.canView(admin) && (
                    <LinkWithParams
                        icon="fa-eye"
                        permissions={[PERMISSION.VIEW_ADMIN]}
                        params={[{ key: `${listName}_show`, value: admin.id }]}
                    />
                )}
                {loggedAdmin.canModify(admin) && (
                    <>
                        <LinkWithParams
                            icon="fa-edit"
                            params={[{ key: `${listName}_edit`, value: admin.id }]}
                            roles={ROLES.MAIN_ADMINS}
                        />
                        <DeleteButton
                            url={`/crea/api/${url}/${admin.id}`}
                            action={updateItems}
                            permissions={[PERMISSION.DELETE_ADMIN]}
                        />
                    </>
                )}
            </>
        );
    }

    return (
        <Card title={cardTitle ?? 'Admins'} removeAnchor>
            <ListItemModalManager
                listName={listName}
                ShowElement={AdminShow}
                EditElement={AdminEdit}
                CreateElement={AdminCreate}
                update={updateItems}
            />
            <Search {...auto.search} />
            <TableLoader isLoading={isLoading}>
                <table className="table model-list">
                    <TableHead>
                        <div />
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Country</th>
                        <th>State</th>
                        <th>Reseller percent revenue</th>
                        <th>Affiliate percent revenue</th>
                        <th>Resellers max debit</th>
                        <th>Roles</th>
                    </TableHead>
                    <tbody>
                        {items.map((admin) => (
                            <tr key={admin.id}>
                                <td className="d-flex">{getLinks(admin, listName)}</td>
                                <td>{admin.id}</td>
                                <td>{admin.name}</td>
                                <td>{admin.email}</td>
                                <td>{admin.country}</td>
                                <td>{admin.state}</td>
                                <td>{admin.reseller_percent_revenue ?? 0}%</td>
                                <td>{admin.aff_percent_revenue ?? 0}%</td>
                                <td>{admin.default_resellers_max_debit_formatted}</td>
                                <td>
                                    {admin.roles?.map((role) => (
                                        <div key={role.id}>{role.name}</div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}
